import faker from 'faker';
import {
	AlertCircle,
	Calendar,
	CheckCircle2,
	Loader2,
	Pause,
	RefreshCw,
	Settings,
	XCircle,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { Toaster, toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select';
import {
	Sheet,
	SheetContent,
	SheetHeader,
	SheetTitle,
} from '@/components/ui/sheet';
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/components/ui/table';

faker.seed(77);

type JobStatus =
	| 'queued'
	| 'processing'
	| 'completed'
	| 'partially_completed'
	| 'failed'
	| 'cancelled';
type FileType = 'customer' | 'account' | 'entity';

type BatchJob = {
	id: string;
	jobId: string;
	fileType: FileType;
	status: JobStatus;
	totalRecords: number;
	ingested: number;
	alerts: number;
	errors: number;
	startTime: string;
	endTime: string | null;
	duration: string;
	createdBy: string;
};

function randomStatus(): JobStatus {
	const statuses: JobStatus[] = [
		'queued',
		'processing',
		'completed',
		'completed',
		'completed',
		'partially_completed',
		'failed',
		'cancelled',
	];
	return statuses[Math.floor(Math.random() * statuses.length)];
}

function randomFileType(): FileType {
	const types: FileType[] = ['customer', 'account', 'entity'];
	return types[Math.floor(Math.random() * types.length)];
}

function makeDuration(status: JobStatus): string {
	if (status === 'queued') return '—';
	const mins = faker.datatype.number({ min: 1, max: 45 });
	const secs = faker.datatype.number({ min: 0, max: 59 });
	return `${mins}m ${secs}s`;
}

const MOCK_JOBS: BatchJob[] = Array.from({ length: 12 }, (_, i) => {
	const status =
		i === 0
			? 'processing'
			: i === 1
				? 'processing'
				: i === 2
					? 'processing'
					: randomStatus();
	const total = faker.datatype.number({ min: 500, max: 50000 });
	const ingested =
		status === 'completed'
			? total
			: faker.datatype.number({ min: 0, max: total });
	const alertCount =
		status === 'failed' || status === 'queued'
			? 0
			: faker.datatype.number({ min: 0, max: 80 });
	const errorCount =
		status === 'failed'
			? faker.datatype.number({ min: 10, max: 200 })
			: faker.datatype.number({ min: 0, max: 5 });
	const startTime = faker.date.recent(7).toISOString();
	return {
		id: faker.datatype.uuid(),
		jobId: `JOB-${1000 + i}`,
		fileType: randomFileType(),
		status,
		totalRecords: total,
		ingested,
		alerts: alertCount,
		errors: errorCount,
		startTime,
		endTime:
			status === 'completed' || status === 'failed'
				? new Date(
						new Date(startTime).getTime() +
							1000 * 60 * faker.datatype.number({ min: 2, max: 50 }),
					).toISOString()
				: null,
		duration: makeDuration(status),
		createdBy: faker.name.findName(),
	};
});

function statusBadge(status: JobStatus) {
	switch (status) {
		case 'queued':
			return (
				<Badge className="border border-gray-200 bg-gray-50 text-gray-600 text-xs hover:bg-gray-50">
					Queued
				</Badge>
			);
		case 'processing':
			return (
				<Badge className="border border-orange-200 bg-orange-50 text-orange-700 text-xs hover:bg-orange-50">
					Processing
				</Badge>
			);
		case 'completed':
			return (
				<Badge className="border border-green-200 bg-green-50 text-green-700 text-xs hover:bg-green-50">
					Completed
				</Badge>
			);
		case 'partially_completed':
			return (
				<Badge className="border border-yellow-200 bg-yellow-50 text-yellow-700 text-xs hover:bg-yellow-50">
					Partial
				</Badge>
			);
		case 'failed':
			return (
				<Badge className="border border-red-200 bg-red-50 text-red-700 text-xs hover:bg-red-50">
					Failed
				</Badge>
			);
		case 'cancelled':
			return (
				<Badge className="border border-gray-200 bg-gray-100 text-gray-500 text-xs hover:bg-gray-100">
					Cancelled
				</Badge>
			);
	}
}

function fileTypeBadge(ft: FileType) {
	switch (ft) {
		case 'customer':
			return (
				<Badge className="border border-purple-200 bg-purple-50 text-purple-700 text-xs hover:bg-purple-50">
					Customer
				</Badge>
			);
		case 'account':
			return (
				<Badge className="border border-cyan-200 bg-cyan-50 text-cyan-700 text-xs hover:bg-cyan-50">
					Account
				</Badge>
			);
		case 'entity':
			return (
				<Badge className="border border-blue-200 bg-blue-50 text-blue-700 text-xs hover:bg-blue-50">
					Entity
				</Badge>
			);
	}
}

function formatDateTime(iso: string): string {
	return new Date(iso).toLocaleString('en-US', {
		month: 'short',
		day: 'numeric',
		year: 'numeric',
		hour: '2-digit',
		minute: '2-digit',
		timeZone: 'UTC',
		timeZoneName: 'short',
	});
}

export default function BatchScreeningPage() {
	const [statusFilter, setStatusFilter] = useState('all');
	const [fileTypeFilter, setFileTypeFilter] = useState('all');
	const [fromDate, setFromDate] = useState('');
	const [toDate, setToDate] = useState('');
	const [selectedJob, setSelectedJob] = useState<BatchJob | null>(null);
	const [sheetOpen, setSheetOpen] = useState(false);
	const [scheduleActive, setScheduleActive] = useState(true);

	const filtered = useMemo(() => {
		return MOCK_JOBS.filter((j) => {
			if (statusFilter !== 'all' && j.status !== statusFilter) return false;
			if (fileTypeFilter !== 'all' && j.fileType !== fileTypeFilter)
				return false;
			return true;
		});
	}, [statusFilter, fileTypeFilter]);

	const totalJobs = MOCK_JOBS.length;
	const processing = MOCK_JOBS.filter((j) => j.status === 'processing').length;
	const completed = MOCK_JOBS.filter((j) => j.status === 'completed').length;
	const failed = MOCK_JOBS.filter((j) => j.status === 'failed').length;

	const handleReset = () => {
		setStatusFilter('all');
		setFileTypeFilter('all');
		setFromDate('');
		setToDate('');
	};

	const handlePause = () => {
		setScheduleActive(false);
		toast.success('Schedule paused');
	};

	const handleSchedule = () => {
		setScheduleActive(true);
		toast.success('Schedule activated');
	};

	return (
		<div className="min-h-screen bg-background">
			<Toaster richColors position="top-right" />
			<div className="border-b border-border bg-white px-6 py-4">
				<div className="flex items-center justify-between">
					<div>
						<h1 className="text-xl font-semibold text-[#0A0A0A]">
							Batch Screening
						</h1>
						<p className="mt-0.5 text-sm text-[#737373]">
							Monitor and manage batch screening jobs
						</p>
					</div>
					<Button
						variant="outline"
						size="sm"
						onClick={() => toast.info('Jobs refreshed')}
					>
						<RefreshCw className="mr-1.5 h-4 w-4" />
						Refresh
					</Button>
				</div>
			</div>

			<div className="p-6 flex flex-col gap-5">
				<div className="flex items-center justify-between rounded-lg border border-[#e5e5e5] bg-white px-4 py-3">
					<div className="flex items-center gap-6 text-sm text-[#525252]">
						<div className="flex items-center gap-2">
							{scheduleActive ? (
								<Badge className="border border-green-200 bg-green-50 text-green-700 text-xs hover:bg-green-50">
									Active
								</Badge>
							) : (
								<Badge variant="secondary" className="text-xs">
									Inactive
								</Badge>
							)}
						</div>
						<span>Frequency: Daily</span>
						<span>Run Times: 02:00, 14:00</span>
						<span>TZ: UTC</span>
						<span>Next: May 27, 2025</span>
						<span>Last: May 26, 2025</span>
					</div>
					<div className="flex items-center gap-2">
						<Button variant="outline" size="sm" onClick={handlePause}>
							<Pause className="mr-1 h-3.5 w-3.5" />
							Pause
						</Button>
						<Button
							size="sm"
							className="bg-[#0A0A0A] text-white hover:bg-[#0A0A0A]/90"
							onClick={handleSchedule}
						>
							<Settings className="mr-1 h-3.5 w-3.5" />
							Schedule
						</Button>
					</div>
				</div>

				<div className="grid grid-cols-4 gap-4">
					<Card>
						<CardContent className="pt-4">
							<div className="flex items-start justify-between">
								<div>
									<p className="text-xs text-[#737373]">Total Jobs</p>
									<p className="mt-1 text-2xl font-bold text-[#0a0a0a]">
										{totalJobs}
									</p>
								</div>
								<Calendar className="h-5 w-5 text-blue-500" />
							</div>
						</CardContent>
					</Card>
					<Card>
						<CardContent className="pt-4">
							<div className="flex items-start justify-between">
								<div>
									<p className="text-xs text-[#737373]">Processing</p>
									<p className="mt-1 text-2xl font-bold text-orange-600">
										{processing}
									</p>
								</div>
								<Loader2 className="h-5 w-5 text-orange-500 animate-spin" />
							</div>
						</CardContent>
					</Card>
					<Card>
						<CardContent className="pt-4">
							<div className="flex items-start justify-between">
								<div>
									<p className="text-xs text-[#737373]">Completed</p>
									<p className="mt-1 text-2xl font-bold text-green-600">
										{completed}
									</p>
								</div>
								<CheckCircle2 className="h-5 w-5 text-green-500" />
							</div>
						</CardContent>
					</Card>
					<Card>
						<CardContent className="pt-4">
							<div className="flex items-start justify-between">
								<div>
									<p className="text-xs text-[#737373]">Failed</p>
									<p className="mt-1 text-2xl font-bold text-red-600">
										{failed}
									</p>
								</div>
								<XCircle className="h-5 w-5 text-red-500" />
							</div>
						</CardContent>
					</Card>
				</div>

				<div className="flex items-center gap-3">
					<Select value={statusFilter} onValueChange={setStatusFilter}>
						<SelectTrigger className="w-[180px]">
							<SelectValue placeholder="Status" />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="all">All Statuses</SelectItem>
							<SelectItem value="queued">Queued</SelectItem>
							<SelectItem value="processing">Processing</SelectItem>
							<SelectItem value="completed">Completed</SelectItem>
							<SelectItem value="partially_completed">
								Partially Completed
							</SelectItem>
							<SelectItem value="failed">Failed</SelectItem>
							<SelectItem value="cancelled">Cancelled</SelectItem>
						</SelectContent>
					</Select>
					<Select value={fileTypeFilter} onValueChange={setFileTypeFilter}>
						<SelectTrigger className="w-[160px]">
							<SelectValue placeholder="File Type" />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="all">All Types</SelectItem>
							<SelectItem value="customer">Customer</SelectItem>
							<SelectItem value="account">Account</SelectItem>
							<SelectItem value="entity">Entity</SelectItem>
						</SelectContent>
					</Select>
					<Input
						type="date"
						value={fromDate}
						onChange={(e) => setFromDate(e.target.value)}
						className="w-[160px]"
						placeholder="From Date"
					/>
					<Input
						type="date"
						value={toDate}
						onChange={(e) => setToDate(e.target.value)}
						className="w-[160px]"
						placeholder="To Date"
					/>
					<Button variant="ghost" size="sm" onClick={handleReset}>
						Reset
					</Button>
				</div>

				<div className="overflow-hidden rounded-xl border border-[#e5e5e5] bg-white">
					<Table>
						<TableHeader>
							<TableRow className="bg-[#fafafa]">
								<TableHead className="text-xs font-semibold text-[#737373]">
									Job ID
								</TableHead>
								<TableHead className="text-xs font-semibold text-[#737373]">
									File Type
								</TableHead>
								<TableHead className="text-xs font-semibold text-[#737373]">
									Status
								</TableHead>
								<TableHead className="text-xs font-semibold text-[#737373]">
									Total Records
								</TableHead>
								<TableHead className="text-xs font-semibold text-[#737373]">
									Ingested
								</TableHead>
								<TableHead className="text-xs font-semibold text-[#737373]">
									Alerts
								</TableHead>
								<TableHead className="text-xs font-semibold text-[#737373]">
									Errors
								</TableHead>
								<TableHead className="text-xs font-semibold text-[#737373]">
									Start Time (UTC)
								</TableHead>
								<TableHead className="text-xs font-semibold text-[#737373]">
									Duration
								</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{filtered.map((job) => (
								<TableRow
									key={job.id}
									className="cursor-pointer hover:bg-[#fafafa]"
									onClick={() => {
										setSelectedJob(job);
										setSheetOpen(true);
									}}
								>
									<TableCell>
										<span className="text-sm font-medium text-blue-600 hover:underline">
											{job.jobId}
										</span>
									</TableCell>
									<TableCell>{fileTypeBadge(job.fileType)}</TableCell>
									<TableCell>{statusBadge(job.status)}</TableCell>
									<TableCell className="text-sm text-[#525252]">
										{job.totalRecords.toLocaleString()}
									</TableCell>
									<TableCell className="text-sm text-[#525252]">
										{job.ingested.toLocaleString()}
									</TableCell>
									<TableCell>
										{job.alerts > 0 ? (
											<Badge className="border border-red-200 bg-red-50 text-red-700 text-xs hover:bg-red-50">
												{job.alerts}
											</Badge>
										) : (
											<span className="text-sm text-[#737373]">0</span>
										)}
									</TableCell>
									<TableCell>
										{job.errors > 0 ? (
											<Badge className="border border-red-200 bg-red-50 text-red-700 text-xs hover:bg-red-50">
												{job.errors}
											</Badge>
										) : (
											<span className="text-sm text-[#737373]">0</span>
										)}
									</TableCell>
									<TableCell className="text-sm text-[#525252]">
										{formatDateTime(job.startTime)}
									</TableCell>
									<TableCell className="text-sm text-[#525252]">
										{job.duration}
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</div>
			</div>

			<Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
				<SheetContent className="w-[480px] sm:max-w-[480px] overflow-y-auto">
					<SheetHeader>
						<SheetTitle>Job Detail: {selectedJob?.jobId}</SheetTitle>
					</SheetHeader>
					{selectedJob && (
						<div className="mt-4 flex flex-col gap-3">
							<div className="overflow-hidden rounded-lg border border-[#e5e5e5]">
								<Table>
									<TableBody>
										{[
											['Job ID', selectedJob.jobId],
											['File Type', selectedJob.fileType],
											['Status', selectedJob.status],
											[
												'Total Records',
												selectedJob.totalRecords.toLocaleString(),
											],
											['Ingested', selectedJob.ingested.toLocaleString()],
											['Alerts', String(selectedJob.alerts)],
											['Errors', String(selectedJob.errors)],
											['Start Time', formatDateTime(selectedJob.startTime)],
											[
												'End Time',
												selectedJob.endTime
													? formatDateTime(selectedJob.endTime)
													: '—',
											],
											['Duration', selectedJob.duration],
											['Created By', selectedJob.createdBy],
										].map(([label, value]) => (
											<TableRow key={label}>
												<TableCell className="text-xs font-medium text-[#737373] w-36">
													{label}
												</TableCell>
												<TableCell className="text-sm text-[#0a0a0a]">
													{value}
												</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</div>
							{selectedJob.errors > 0 && (
								<div className="flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2">
									<AlertCircle className="h-4 w-4 text-red-600 shrink-0" />
									<p className="text-xs text-red-700">
										{selectedJob.errors} error(s) occurred during ingestion.
										Check logs for details.
									</p>
								</div>
							)}
						</div>
					)}
				</SheetContent>
			</Sheet>
		</div>
	);
}
